/**
 * Public marketing-surface routing contract.
 *
 * `codeStatus: "implemented"` means the surface exists in this branch's
 * source. It does not claim that the surface is deployed or live.
 *
 * Experience display names are owner-approved customer-facing names. They do
 * not derive durable Experience ids, routes, commerce Offers, analytics
 * identities, runtime product ids, or client entitlements. The client
 * entitlement contract remains exactly `report` and `dynamic`, owned by
 * useEntitlements.ts.
 */

import {
  DTR_CAMPAIGNS,
  DTR_ORDER,
} from '../components/public/landing-sections/dtrCampaigns.js';

export const PUBLIC_SURFACE_SCHEMA_VERSION = 3;
export const ADU_REPORT_TEMPLATE_ID = 'data_drop_micro_conversion';
export const ADU_REPORT_LANDING_ID = 'adu_lab_report';

/**
 * DTR keys are implementation-local render/selector aliases. In particular,
 * `default` must never leave Orca as a public, campaign, Dash, landing, or
 * destination identity.
 */
export const ADU_REPORT_IMPLEMENTATION_MESSAGE_KEYS = Object.freeze([
  ...DTR_ORDER,
]);

/**
 * Compatibility export for the staging/internal TemplateToolbar. New
 * acquisition code must use ADU_REPORT_DURABLE_MESSAGE_KEYS instead.
 */
export const ADU_REPORT_MESSAGE_KEYS = ADU_REPORT_IMPLEMENTATION_MESSAGE_KEYS;

export const ADU_REPORT_MESSAGE_CONFIGS = Object.freeze([
  Object.freeze({
    implementationMessageKey: 'default',
    messageKey: 'adu_standard',
    messageDisplayName: 'ADU Standard',
    messageSlug: null,
    destinationId: 'adu_lab_report_adu_standard',
    paidCampaignDestinationAuthorized: true,
  }),
  Object.freeze({
    implementationMessageKey: 'generational-living',
    messageKey: 'generational_living',
    messageDisplayName: 'Generational Living',
    messageSlug: 'generational-living',
    destinationId: 'adu_lab_report_generational_living',
    paidCampaignDestinationAuthorized: true,
  }),
  Object.freeze({
    implementationMessageKey: 'imagine-possibility',
    messageKey: 'imagine_possibility',
    messageDisplayName: 'Imagine Possibility',
    messageSlug: 'imagine-possibility',
    destinationId: 'adu_lab_report_imagine_possibility',
    paidCampaignDestinationAuthorized: true,
  }),
  Object.freeze({
    implementationMessageKey: 'lifestyle-studio',
    messageKey: 'lifestyle_studio',
    messageDisplayName: 'Lifestyle Studio',
    messageSlug: 'lifestyle-studio',
    destinationId: 'adu_lab_report_lifestyle_studio',
    paidCampaignDestinationAuthorized: true,
  }),
  Object.freeze({
    implementationMessageKey: 'potential-unlocked',
    messageKey: 'potential_unlocked',
    messageDisplayName: 'Potential Unlocked',
    messageSlug: 'potential-unlocked',
    destinationId: 'adu_lab_report_potential_unlocked',
    paidCampaignDestinationAuthorized: true,
  }),
  Object.freeze({
    implementationMessageKey: 'all-roads-lead-home',
    messageKey: 'all_roads_lead_home',
    messageDisplayName: 'All Roads Lead to Home',
    messageSlug: 'all-roads-lead-home',
    destinationId: 'adu_lab_report_all_roads_lead_home',
    paidCampaignDestinationAuthorized: true,
  }),
]);

export const ADU_REPORT_DURABLE_MESSAGE_KEYS = Object.freeze(
  ADU_REPORT_MESSAGE_CONFIGS.map(({ messageKey }) => messageKey),
);

export const ADU_REPORT_MESSAGE_SLUGS = Object.freeze(
  Object.fromEntries(
    ADU_REPORT_MESSAGE_CONFIGS.map(({ messageKey, messageSlug }) => [
      messageKey,
      messageSlug,
    ]),
  ),
);

const ADU_REPORT_MESSAGE_BY_IMPLEMENTATION_KEY = new Map(
  ADU_REPORT_MESSAGE_CONFIGS.map((config) => [
    config.implementationMessageKey,
    config,
  ]),
);
const ADU_REPORT_MESSAGE_BY_DURABLE_KEY = new Map(
  ADU_REPORT_MESSAGE_CONFIGS.map((config) => [config.messageKey, config]),
);
const ADU_REPORT_MESSAGE_BY_SLUG = new Map(
  ADU_REPORT_MESSAGE_CONFIGS
    .filter(({ messageSlug }) => messageSlug)
    .map((config) => [config.messageSlug, config]),
);
const ADU_REPORT_MESSAGE_BY_DESTINATION_ID = new Map(
  ADU_REPORT_MESSAGE_CONFIGS.map((config) => [config.destinationId, config]),
);
const ADU_REPORT_FIXED_ACQUISITION_IDENTITY = Object.freeze({
  productKey: 'adu_lab',
  cartridgeKey: 'adu_residential',
  surfaceKey: 'report',
  experienceDisplayName: 'Report',
  templateId: ADU_REPORT_TEMPLATE_ID,
  landingId: ADU_REPORT_LANDING_ID,
  renderEngine: 'DefaultLandingPage',
});

export const ADU_PRODUCT_HOMEPAGE_SURFACE = Object.freeze({
  schemaVersion: PUBLIC_SURFACE_SCHEMA_VERSION,
  surfaceKey: 'product_homepage',
  surfaceKind: 'product_homepage',
  experienceDisplayName: null,
  experienceRole: null,
  experienceId: null,
  editionId: null,
  codeStatus: 'implemented',
  featureFlagKey: null,
  canonicalPath: '/',
  trafficRoles: Object.freeze([
    'organic_discovery',
    'direct_visit',
    'return_exploration',
  ]),
  paidCampaignDestinationAuthorized: false,
  analytics: Object.freeze({
    surface: 'product_homepage',
  }),
});

export const ADU_REPORT_SURFACE = Object.freeze({
  schemaVersion: PUBLIC_SURFACE_SCHEMA_VERSION,
  surfaceKey: 'report',
  surfaceKind: 'marketing_landing',
  experienceDisplayName: 'Report',
  experienceRole: 'answer_clarity',
  experienceId: 'report',
  editionId: 'static',
  codeStatus: 'implemented',
  featureFlagKey: null,
  canonicalPath: '/report',
  fallbackMessageKey: 'adu_standard',
  implementationFallbackMessageKey: 'default',
  messageSlugs: ADU_REPORT_MESSAGE_SLUGS,
  templateId: ADU_REPORT_TEMPLATE_ID,
  landingId: ADU_REPORT_LANDING_ID,
  destinations: ADU_REPORT_MESSAGE_CONFIGS,
  workingTemplateLabel: 'Data-Drop Micro-Conversion',
  renderEngine: 'DefaultLandingPage',
  sourceLocator: 'app/src/pages/DefaultLandingPage.jsx',
  offerKey: null,
  entitlementKey: null,
  conversionDestinations: Object.freeze({
    leadFirstPreviewPath: '/welcome',
    defaultPath: '/checkout/report',
  }),
  analytics: Object.freeze({
    surface: 'report_landing_page',
    destinationId: null,
    landingId: ADU_REPORT_LANDING_ID,
    templateId: ADU_REPORT_TEMPLATE_ID,
    renderEngine: 'DefaultLandingPage',
  }),
});

export const ADU_PUBLIC_SURFACE_PROPOSALS = Object.freeze([
  Object.freeze({
    schemaVersion: PUBLIC_SURFACE_SCHEMA_VERSION,
    implementationLocalKey: 'proposal:studio',
    persistedId: null,
    surfaceKey: null,
    experienceDisplayName: 'Studio',
    experienceRole: null,
    experienceId: null,
    codeStatus: 'proposal',
    featureFlagKey: null,
    route: null,
    offerKey: null,
    entitlementKey: null,
    analyticsIdentity: null,
  }),
  Object.freeze({
    schemaVersion: PUBLIC_SURFACE_SCHEMA_VERSION,
    implementationLocalKey: 'proposal:academy',
    persistedId: null,
    surfaceKey: null,
    experienceDisplayName: 'Academy',
    experienceRole: null,
    experienceId: null,
    codeStatus: 'proposal',
    featureFlagKey: null,
    route: null,
    offerKey: null,
    entitlementKey: null,
    analyticsIdentity: null,
  }),
]);

export const PUBLIC_SURFACE_REGISTRY = Object.freeze({
  schemaVersion: PUBLIC_SURFACE_SCHEMA_VERSION,
  products: Object.freeze({
    adu_lab: Object.freeze({
      productKey: 'adu_lab',
      productName: 'ADU Lab',
      cartridgeKey: 'adu_residential',
      productionHost: 'adulab.app',
      productHomepage: ADU_PRODUCT_HOMEPAGE_SURFACE,
      surfaces: Object.freeze([ADU_REPORT_SURFACE]),
      surfaceProposals: ADU_PUBLIC_SURFACE_PROPOSALS,
    }),
  }),
});

export const ADU_LAB_PUBLIC_PRODUCT = PUBLIC_SURFACE_REGISTRY.products.adu_lab;

export function getPublicSurfaceOptions(productKey = 'adu_lab') {
  const product = PUBLIC_SURFACE_REGISTRY.products[productKey];
  if (!product) return [];

  const implementedOptions = product.surfaces.map((surface) => ({
    value: surface.surfaceKey,
    label: surface.experienceDisplayName,
    disabled: surface.codeStatus !== 'implemented',
  }));
  const proposalOptions = product.surfaceProposals.map((proposal) => ({
    value: proposal.implementationLocalKey,
    label: proposal.experienceDisplayName,
    disabled: true,
  }));

  return [...implementedOptions, ...proposalOptions];
}

function stripQueryHashAndTrailingSlash(value) {
  if (typeof value !== 'string' || !value.startsWith('/')) return null;
  const [pathOnly] = value.split(/[?#]/, 1);
  if (!pathOnly) return null;
  return pathOnly.length > 1 ? pathOnly.replace(/\/+$/, '') : pathOnly;
}

function buildResolvedReportRoute(messageConfig, requestedPath) {
  const { messageSlug } = messageConfig;
  const canonicalPath = messageSlug
    ? `${ADU_REPORT_SURFACE.canonicalPath}/${messageSlug}`
    : ADU_REPORT_SURFACE.canonicalPath;

  return Object.freeze({
    productKey: ADU_LAB_PUBLIC_PRODUCT.productKey,
    productName: ADU_LAB_PUBLIC_PRODUCT.productName,
    cartridgeKey: ADU_LAB_PUBLIC_PRODUCT.cartridgeKey,
    surfaceKey: ADU_REPORT_SURFACE.surfaceKey,
    surfaceKind: ADU_REPORT_SURFACE.surfaceKind,
    experienceDisplayName: ADU_REPORT_SURFACE.experienceDisplayName,
    experienceId: ADU_REPORT_SURFACE.experienceId,
    editionId: ADU_REPORT_SURFACE.editionId,
    implementationMessageKey: messageConfig.implementationMessageKey,
    messageKey: messageConfig.messageKey,
    messageKeyApplied: messageConfig.messageKey,
    messageDisplayName: messageConfig.messageDisplayName,
    messageSlug,
    templateId: ADU_REPORT_SURFACE.templateId,
    landingId: ADU_REPORT_SURFACE.landingId,
    destinationId: messageConfig.destinationId,
    renderEngine: ADU_REPORT_SURFACE.renderEngine,
    paidCampaignDestinationAuthorized:
      messageConfig.paidCampaignDestinationAuthorized,
    canonicalPath,
    isCanonicalPath: requestedPath === canonicalPath,
  });
}

/**
 * Resolve only the explicitly public ADU Report marketing surfaces.
 *
 * This intentionally rejects `/report/:projectId/:shareToken` and every
 * `/report-*` application route. Never replace it with startsWith('/report').
 */
export function resolvePublicSurfacePath(pathname) {
  const requestedPath = typeof pathname === 'string'
    ? pathname.split(/[?#]/, 1)[0]
    : null;
  const normalizedPath = stripQueryHashAndTrailingSlash(pathname);
  if (!normalizedPath || !requestedPath) return null;

  if (normalizedPath === ADU_REPORT_SURFACE.canonicalPath) {
    return buildResolvedReportRoute(
      ADU_REPORT_MESSAGE_BY_IMPLEMENTATION_KEY.get(
        ADU_REPORT_SURFACE.implementationFallbackMessageKey,
      ),
      requestedPath,
    );
  }

  const messagePrefix = `${ADU_REPORT_SURFACE.canonicalPath}/`;
  if (!normalizedPath.startsWith(messagePrefix)) return null;

  const messageSlug = normalizedPath.slice(messagePrefix.length);
  if (!messageSlug || messageSlug.includes('/')) return null;
  const messageConfig = ADU_REPORT_MESSAGE_BY_SLUG.get(messageSlug);
  if (!messageConfig) return null;

  return buildResolvedReportRoute(messageConfig, requestedPath);
}

export function isPublicSurfacePath(pathname) {
  return resolvePublicSurfacePath(pathname) !== null;
}

/**
 * A one-segment `/report/<slug>` path belongs to the marketing namespace even
 * when the slug is invalid. Routing it through the public tree prevents
 * AppShell exposure; the public route boundary must then reject it rather than
 * silently applying the baseline message.
 */
export function isReservedReportMarketingShape(pathname) {
  const normalizedPath = stripQueryHashAndTrailingSlash(pathname);
  if (!normalizedPath) return false;

  const messagePrefix = `${ADU_REPORT_SURFACE.canonicalPath}/`;
  if (!normalizedPath.startsWith(messagePrefix)) return false;

  const suffix = normalizedPath.slice(messagePrefix.length);
  return suffix.length > 0 && !suffix.includes('/');
}

export function buildPublicSurfacePath({
  productKey = 'adu_lab',
  surfaceKey = 'report',
  messageKey = 'adu_standard',
  implementationMessageKey,
} = {}) {
  if (productKey !== ADU_LAB_PUBLIC_PRODUCT.productKey) return null;
  if (surfaceKey !== ADU_REPORT_SURFACE.surfaceKey) return null;

  const messageConfig = implementationMessageKey
    ? ADU_REPORT_MESSAGE_BY_IMPLEMENTATION_KEY.get(implementationMessageKey)
    : ADU_REPORT_MESSAGE_BY_DURABLE_KEY.get(messageKey);
  if (!messageConfig) return null;

  const { messageSlug } = messageConfig;
  return messageSlug
    ? `${ADU_REPORT_SURFACE.canonicalPath}/${messageSlug}`
    : ADU_REPORT_SURFACE.canonicalPath;
}

export function resolveDurableReportMessageKey(value) {
  if (typeof value !== 'string') return null;
  const normalizedValue = value.trim().toLowerCase();
  if (!normalizedValue) return null;
  return (
    ADU_REPORT_MESSAGE_BY_DURABLE_KEY.get(normalizedValue)?.messageKey ||
    ADU_REPORT_MESSAGE_BY_IMPLEMENTATION_KEY.get(normalizedValue)?.messageKey ||
    ADU_REPORT_MESSAGE_BY_SLUG.get(normalizedValue)?.messageKey ||
    null
  );
}

/**
 * Validate the durable ADU Report acquisition identity as one closed tuple.
 * Callers may keep unrelated/legacy identity vocabularies when
 * isReportIdentityClaim is false. Once any ADU Report lineage field is claimed,
 * an unknown or mismatched field fails closed and the tuple must be omitted.
 */
export function reconcileAduReportAcquisitionIdentity(input = {}) {
  const normalizedInput = Object.fromEntries(
    Object.entries(input).map(([key, value]) => [
      key,
      typeof value === 'string' ? value.trim() : value,
    ]),
  );
  const destinationId = normalizedInput.destinationId;
  const isReportIdentityClaim =
    normalizedInput.landingId === ADU_REPORT_LANDING_ID ||
    (typeof destinationId === 'string' &&
      destinationId.startsWith(`${ADU_REPORT_LANDING_ID}_`)) ||
    (normalizedInput.surfaceKey === 'report' &&
      (normalizedInput.productKey === 'adu_lab' ||
        normalizedInput.cartridgeKey === 'adu_residential'));

  if (!isReportIdentityClaim) {
    return Object.freeze({ isReportIdentityClaim: false, identity: null });
  }

  const candidates = [];
  for (const value of [
    normalizedInput.messageKey,
    normalizedInput.messageKeyApplied,
    normalizedInput.implementationMessageKey,
    normalizedInput.messageSlug,
  ]) {
    if (!value) continue;
    const durableMessageKey = resolveDurableReportMessageKey(value);
    const messageConfig = durableMessageKey
      ? ADU_REPORT_MESSAGE_BY_DURABLE_KEY.get(durableMessageKey)
      : null;
    if (!messageConfig) {
      return Object.freeze({ isReportIdentityClaim: true, identity: null });
    }
    candidates.push(messageConfig);
  }

  if (destinationId) {
    const destinationConfig =
      ADU_REPORT_MESSAGE_BY_DESTINATION_ID.get(destinationId);
    if (!destinationConfig) {
      return Object.freeze({ isReportIdentityClaim: true, identity: null });
    }
    candidates.push(destinationConfig);
  }

  const uniqueMessageKeys = new Set(
    candidates.map(({ messageKey }) => messageKey),
  );
  if (uniqueMessageKeys.size !== 1) {
    return Object.freeze({ isReportIdentityClaim: true, identity: null });
  }

  for (const [key, expectedValue] of Object.entries(
    ADU_REPORT_FIXED_ACQUISITION_IDENTITY,
  )) {
    if (
      normalizedInput[key] !== undefined &&
      normalizedInput[key] !== null &&
      normalizedInput[key] !== '' &&
      normalizedInput[key] !== expectedValue
    ) {
      return Object.freeze({ isReportIdentityClaim: true, identity: null });
    }
  }

  const messageConfig = candidates[0];
  return Object.freeze({
    isReportIdentityClaim: true,
    identity: Object.freeze({
      ...ADU_REPORT_FIXED_ACQUISITION_IDENTITY,
      messageKey: messageConfig.messageKey,
      messageKeyApplied: messageConfig.messageKey,
      destinationId: messageConfig.destinationId,
    }),
  });
}

export function hasMatchingDtrMessageRegistry() {
  return ADU_REPORT_IMPLEMENTATION_MESSAGE_KEYS.every(
    (messageKey) => DTR_CAMPAIGNS[messageKey],
  );
}
